/**
 * FilmGrid component displays a grid of film posters
 * Similar to Letterboxd's poster grid layout
 */

export interface Film {
  id: string;
  title: string;
  year: string;
  tmdbId: number;
  posterUrl: string;
  addedAt: string | null;
}

interface FilmGridProps {
  films: Film[];
  onRemoveFilm?: (filmId: string) => void;
  isManualMode?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function FilmGrid({
  films,
  onRemoveFilm,
  isManualMode = false,
  isLoading = false,
  emptyMessage = 'No films in this watchlist yet.',
}: FilmGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i}>
            <div className="aspect-[2/3] bg-gray-200 rounded animate-pulse" />
            <div className="mt-2 h-4 bg-gray-200 rounded animate-pulse" />
            <div className="mt-1 h-3 w-12 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (films.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {films.map((film) => (
        <FilmPoster
          key={film.id}
          film={film}
          onRemove={
            isManualMode && onRemoveFilm
              ? () => onRemoveFilm(film.id)
              : undefined
          }
        />
      ))}
    </div>
  );
}

interface FilmPosterProps {
  film: Film;
  onRemove?: () => void;
}

function FilmPoster({ film, onRemove }: FilmPosterProps) {
  const placeholderUrl = `https://via.placeholder.com/200x300/374151/9CA3AF?text=${encodeURIComponent(film.title.substring(0, 10))}`;

  return (
    <div className="group">
      {/* Poster container */}
      <div className="aspect-[2/3] relative overflow-hidden rounded shadow-md hover:shadow-lg transition-shadow">
        <img
          src={film.posterUrl || placeholderUrl}
          alt={`${film.title} (${film.year})`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = placeholderUrl;
          }}
        />

        {/* Remove button (only in manual mode) */}
        {onRemove && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute top-1 right-1 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-sm font-bold"
            title={`Remove ${film.title}`}
          >
            &times;
          </button>
        )}
      </div>

      {/* Title and year underneath poster */}
      <div className="mt-2">
        <p className="text-sm font-medium text-gray-900 line-clamp-1">
          {film.title}
        </p>
        <p className="text-xs text-gray-500">{film.year}</p>
      </div>
    </div>
  );
}
